import { useRef, useState, useCallback, useEffect } from 'react';

type Placement =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-start'
  | 'top-end'
  | 'bottom-start'
  | 'bottom-end'
  | 'left-start'
  | 'left-end'
  | 'right-start'
  | 'right-end';

interface FloatingPosition {
  top: number;
  left: number;
}

interface UseFloatingOptions {
  /** Where to place the floating element relative to the trigger */
  placement?: Placement;
  /** Pixel offset from the trigger edge */
  offset?: number;
  /** Whether the floating element is currently shown */
  open?: boolean;
}

interface UseFloatingReturn<T extends HTMLElement, F extends HTMLElement> {
  triggerRef: React.RefObject<T | null>;
  floatingRef: React.RefObject<F | null>;
  position: FloatingPosition;
  /** Manually recalculate position */
  update: () => void;
}

/**
 * useFloating — positions a floating element relative to a trigger element.
 *
 * Handles viewport collision detection and flips the placement when the
 * floating element would overflow the viewport. Recalculates on scroll
 * and resize when `open` is true.
 *
 * Used by: Tooltip, Popover, Select, Dropdown, Cascader, TreeSelect, Mentions.
 *
 * @example
 * const { triggerRef, floatingRef, position } = useFloating<HTMLButtonElement, HTMLDivElement>({
 *   placement: 'bottom',
 *   offset: 8,
 *   open: isOpen,
 * });
 *
 * <button ref={triggerRef}>Trigger</button>
 * {isOpen && (
 *   <Portal>
 *     <div ref={floatingRef} style={{ position: 'fixed', top: position.top, left: position.left }}>
 *       Floating content
 *     </div>
 *   </Portal>
 * )}
 */
export function useFloating<T extends HTMLElement = HTMLElement, F extends HTMLElement = HTMLElement>(
  options: UseFloatingOptions = {},
): UseFloatingReturn<T, F> {
  const { placement = 'bottom', offset = 8, open = false } = options;

  const triggerRef = useRef<T | null>(null);
  const floatingRef = useRef<F | null>(null);
  const [position, setPosition] = useState<FloatingPosition>({ top: -9999, left: -9999 });

  const update = useCallback(() => {
    const trigger = triggerRef.current;
    const floating = floatingRef.current;
    if (!trigger || !floating) return;

    const tRect = trigger.getBoundingClientRect();
    const fRect = floating.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let top = 0;
    let left = 0;

    const [side, align] = placement.split('-') as [string, string | undefined];

    // Calculate primary position
    switch (side) {
      case 'top':
        top = tRect.top - fRect.height - offset;
        left = tRect.left + (tRect.width - fRect.width) / 2;
        break;
      case 'bottom':
        top = tRect.bottom + offset;
        left = tRect.left + (tRect.width - fRect.width) / 2;
        break;
      case 'left':
        top = tRect.top + (tRect.height - fRect.height) / 2;
        left = tRect.left - fRect.width - offset;
        break;
      case 'right':
        top = tRect.top + (tRect.height - fRect.height) / 2;
        left = tRect.right + offset;
        break;
    }

    // Apply alignment
    if (align === 'start') {
      if (side === 'top' || side === 'bottom') left = tRect.left;
      else top = tRect.top;
    } else if (align === 'end') {
      if (side === 'top' || side === 'bottom') left = tRect.right - fRect.width;
      else top = tRect.bottom - fRect.height;
    }

    // Viewport collision — flip if needed
    if (side === 'bottom' && top + fRect.height > vh) {
      top = tRect.top - fRect.height - offset;
    } else if (side === 'top' && top < 0) {
      top = tRect.bottom + offset;
    } else if (side === 'right' && left + fRect.width > vw) {
      left = tRect.left - fRect.width - offset;
    } else if (side === 'left' && left < 0) {
      left = tRect.right + offset;
    }

    // Clamp to viewport edges
    top = Math.max(4, Math.min(top, vh - fRect.height - 4));
    left = Math.max(4, Math.min(left, vw - fRect.width - 4));

    setPosition({ top, left });
  }, [placement, offset]);

  // Recalculate on open, scroll, resize
  useEffect(() => {
    if (!open) return;

    // Use rAF to wait for floating element to render
    const raf = requestAnimationFrame(update);

    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, update]);

  return { triggerRef, floatingRef, position, update };
}
